import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../../../shared/components/toast/services/toast.service';
import { ChatApi } from '../../apis/chat.api';
import { ChatAttachmentsApi } from '../../apis/chat-attachments.api';
import { EnumAttachmentStatus } from '../../enums/attachment-status.enum';
import { EnumAttachmentType } from '../../enums/attachment-type.enum';
import {
  IMessageAttachment,
  isUserAttachment
} from '../../interfaces/message-attachment.interface';
import { isAttachmentDownloadable } from '../../interfaces/user-attachment.interface';
import { attachmentErrorMessage } from '../../utils/attachment-error-message.util';
import { getAttachmentType } from '../../utils/attachment.utils';

@Component({
  selector: 'sgt-message-attachment',
  templateUrl: './message-attachment.component.html',
  styleUrls: ['./message-attachment.component.scss']
})
export class MessageAttachmentComponent implements OnChanges {
  @Input() attachment: IMessageAttachment;
  @Input() origin?: string;
  attachmentType: EnumAttachmentType = EnumAttachmentType.GENERIC;
  isLoading = false;
  canDownload = true;
  isProcessing = false;
  unavailableReason: string | null = null;

  readonly EnumAttachmentType = EnumAttachmentType;

  constructor(
    private chatApi: ChatApi,
    private chatAttachmentsApi: ChatAttachmentsApi,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.attachment || !this.attachment) {
      return;
    }
    this.attachmentType = getAttachmentType(
      this.attachment.name,
      this.attachment.mime
    );
    this.applyAvailability();
  }

  onDownload(): void {
    if (this.isLoading || !this.canDownload) {
      return;
    }

    const attachment = this.attachment;
    if (!isUserAttachment(attachment) && attachment.accessMode === 'signed' && attachment.url) {
      this.triggerDownload(attachment.url);
      return;
    }

    this.fetchBlob().subscribe({
      next: (blob) => this.triggerDownload(URL.createObjectURL(blob), true),
      error: () => this.toastService.error('Não foi possível baixar o arquivo.')
    });
  }

  private applyAvailability(): void {
    if (!isUserAttachment(this.attachment)) {
      this.canDownload = true;
      this.isProcessing = false;
      this.unavailableReason = null;
      return;
    }
    const falhou =
      this.attachment.status === EnumAttachmentStatus.Failed ||
      this.attachment.status === EnumAttachmentStatus.Deleted;
    // Sem `attachmentId` o upload ainda corre — a menos que já tenha falhado.
    this.isProcessing =
      !falhou &&
      (!this.attachment.attachmentId ||
        this.attachment.status === EnumAttachmentStatus.Processing);
    this.canDownload = isAttachmentDownloadable(this.attachment);
    this.unavailableReason =
      this.canDownload || this.isProcessing
        ? null
        : this.attachment.errorCode
          ? attachmentErrorMessage(
              this.attachment.errorCode,
              this.attachment.errorMessage
            )
          : 'Não foi possível enviar o arquivo';
  }

  private fetchBlob(): Observable<Blob> {
    this.isLoading = true;
    const attachment = this.attachment;
    const source$ = isUserAttachment(attachment)
      ? this.chatAttachmentsApi.downloadSource(attachment.attachmentId)
      : this.chatApi.downloadFile(attachment.objectPath);
    return source$.pipe(finalize(() => (this.isLoading = false)));
  }

  private triggerDownload(href: string, revoke = false): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = this.attachment.name;
    link.click();
    // Revogar no mesmo tick cancela o download em alguns navegadores.
    if (revoke) {
      setTimeout(() => URL.revokeObjectURL(href), 0);
    }
  }
}
